import React, { useState } from "react";
import { useConsentData } from "../../hooks/useConsentData";
import { ConsentModal } from "../../components/consent/ConsentModal";
import { CircularProgress, Alert } from "@carrier-io/air-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RoutesPage } from "../../constants/routes";
import { deleteLoginCookies } from "../../utils/cookie";
import { useGetCurrentUserConsents } from "../../hooks/useGetCurrentUserConsents";
import Box from "@carrier-io/air-react/Box";
import { AWSRegions } from "../../generated/admin/graphql";
import { DEFAULT_REGION } from "../../constants/regions";
import { useUpdateUser } from "../../hooks/useUpdateUser";
import { useGetCurrentUser } from "../../hooks/useGetCurrentUser";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes } from "../../contexts/alert/types";

export const ConsentPage = () => {
  const navigate = useNavigate();
  const [consentHandlingError, setConsentHandlingError] =
    useState<Error | null>();
  const {
    isLoading,
    error: consentDownloadError,
    consents,
  } = useGetCurrentUserConsents();
  const { pushAlert } = useAlerts();
  const currentUser = useGetCurrentUser();
  const consentData = useConsentData();
  const [updateUser] = useUpdateUser();
  const [searchParams] = useSearchParams();

  const successHandler = () => {
    if (!currentUser) {
      pushAlert(
        AlertTypes.ERROR,
        "Failed to accept consents. User data is not available.",
      );
      navigate({
        pathname: RoutesPage.Login,
        search: searchParams.toString(),
      });
      return;
    }

    updateUser({
      variables: {
        username: currentUser.username ?? "",
        region: (currentUser.region as AWSRegions) || DEFAULT_REGION,
        input: {
          lastLogin: new Date().toISOString(),
        },
      },
    });

    navigate({
      pathname: RoutesPage.Dashboard,
      search: searchParams.toString(),
    });
  };

  const errorHandler = (error: Error) => {
    setConsentHandlingError(error);
  };

  const declineHandler = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.append(
      "prevError",
      "Acceptance of Terms of Service and Privacy Policy is required in order to proceed.",
    );
    deleteLoginCookies(window.location.hostname);

    navigate({
      pathname: RoutesPage.Login,
      search: updatedSearchParams.toString(),
    });
  };

  const restartLogin = () => {
    deleteLoginCookies(window.location.hostname);
    navigate({ pathname: RoutesPage.Login, search: searchParams.toString() });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <CircularProgress size="70px" />
      </Box>
    );
  }

  const error = consentDownloadError || consentHandlingError;

  if (error) {
    return (
      <Alert severity="error" variant="outlined" onClose={restartLogin}>
        {error.message}
      </Alert>
    );
  }

  const acceptedConsents = new Set(
    consents ? consents.filter((c) => c.accepted).map((c) => c.name) : [],
  );

  const consentsToAccept = consentData.filter(
    (c) => !acceptedConsents.has(c.file),
  );

  if (!consentsToAccept.length) {
    successHandler();
    return null;
  }

  return (
    <ConsentModal
      consents={consentsToAccept}
      onSuccess={successHandler}
      onError={errorHandler}
      onDecline={declineHandler}
    />
  );
};
